// src/contexts/BookmarksContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import { collection, doc, setDoc, deleteDoc, onSnapshot, serverTimestamp, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { AuthContext } from './AuthContext';

export const BookmarksContext = createContext({
  bookmarks: [],
  addBookmark: () => {},
  removeBookmark: () => {},
  isBookmarked: () => false,
  clearBookmarks: () => {},
});

export function BookmarksProvider({ children }) {
  const [bookmarks, setBookmarks] = useState([]);
  const { user } = useContext(AuthContext);

  // Listen to Firestore bookmarks when signed in, clear when signed out
  useEffect(() => {
    if (!user) {
      setBookmarks([]);
      return;
    }

    const bookmarksRef = collection(db, 'users', user.uid, 'bookmarks');
    const unsubscribe = onSnapshot(bookmarksRef, (snapshot) => {
      const items = snapshot.docs.map((d) => ({
        id: d.id,
        productID: d.data().productID || d.id,
      }));
      setBookmarks(items);
    });

    return () => unsubscribe();
  }, [user]);

  const addBookmark = (productID) => {
    if (!user) return;
    const ref = doc(db, 'users', user.uid, 'bookmarks', productID);
    setDoc(ref, { productID, createdAt: serverTimestamp() });
  };

  const removeBookmark = (productID) => {
    if (!user) return;
    const ref = doc(db, 'users', user.uid, 'bookmarks', productID);
    deleteDoc(ref);
  };

  const isBookmarked = (productID) => {
    return bookmarks.some((b) => b.productID === productID);
  };

  const clearBookmarks = async () => {
    if (!user) return;
    const bookmarksRef = collection(db, 'users', user.uid, 'bookmarks');
    const snapshot = await getDocs(bookmarksRef);
    snapshot.docs.forEach((d) => deleteDoc(d.ref));
  };

  const value = {
    bookmarks,
    addBookmark,
    removeBookmark,
    isBookmarked,
    clearBookmarks,
  };

  return (
    <BookmarksContext.Provider value={value}>
      {children}
    </BookmarksContext.Provider>
  );
}
